import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Zap, LayoutDashboard, Ticket, Calendar, Users, 
  MessageSquare, LogOut, Menu, X, ChevronRight, Shield
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Button } from '../../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [checking, setChecking] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  useEffect(() => {
    checkAdminAccess();
  }, [user]);

  const checkAdminAccess = async () => {
    if (!user) {
      setChecking(false);
      return;
    }

    try {
      const res = await fetch(`${API}/admin/stats`, { credentials: 'include' });
      if (res.ok) {
        setIsAdmin(true);
      } else if (res.status === 403) {
        setIsAdmin(false);
      }
    } catch (error) {
      setIsAdmin(false);
    } finally {
      setChecking(false);
    }
  };

  const navItems = [
    { name: 'Dashboard', href: '/admin', icon: LayoutDashboard },
    { name: 'Tickets', href: '/admin/tickets', icon: Ticket },
    { name: 'Bookings', href: '/admin/bookings', icon: Calendar },
    { name: 'Users', href: '/admin/users', icon: Users },
    { name: 'Inquiries', href: '/admin/inquiries', icon: MessageSquare },
  ];

  const isActive = (path) => {
    if (path === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(path);
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#080B14' }}>
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Verifying admin access...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#080B14' }}>
        <div className="text-center max-w-md mx-auto px-6">
          <div className="w-20 h-20 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-6 border border-red-500/25">
            <Shield className="w-10 h-10 text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-3">Admin Access Required</h1>
          <p className="text-gray-400 mb-6 leading-relaxed">
            You don't have permission to access the admin dashboard. Please contact your administrator.
          </p>
          <div className="flex gap-4 justify-center">
            <Button onClick={() => navigate('/dashboard')} variant="outline" className="border-blue-500/20 text-white bg-transparent hover:bg-blue-500/5">
              Go to Dashboard
            </Button>
            <Button onClick={() => navigate('/')} className="bg-gradient-to-r from-blue-500 to-cyan-400 text-white font-semibold border-none">
              Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#080B14' }}>
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 glass h-16 flex items-center justify-between px-4 border-t-0 border-x-0 border-b border-blue-500/10">
        <Link to="/admin" className="flex items-center gap-2">
          <img src="/logo.png" alt="NetEye Logo" className="w-8 h-8 rounded-lg object-cover shadow-[0_0_10px_rgba(59,130,246,0.2)]" />
          <span className="font-semibold text-lg bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">Admin</span>
        </Link>
        <button 
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 text-gray-300 hover:text-white"
        >
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Sidebar Overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="lg:hidden fixed inset-0 bg-black/60 z-40 backdrop-blur-sm"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full w-64 z-50 
        transform transition-transform duration-300 ease-in-out
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `} style={{ background: '#0D1224', borderRight: '1px solid rgba(99, 130, 255, 0.12)' }}>
        {/* Logo */}
        <div className="h-20 flex items-center px-6 border-b border-blue-500/10">
          <Link to="/admin" className="flex items-center gap-3">
            <img src="/logo.png" alt="NetEye Logo" className="w-10 h-10 rounded-xl object-cover shadow-[0_0_15px_rgba(59,130,246,0.2)]" />
            <div>
              <span className="font-semibold text-lg tracking-tight block bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">Admin Panel</span>
              <span className="text-xs text-blue-400 font-bold tracking-widest uppercase" style={{ fontFamily: "'Exo 2', sans-serif" }}>NETEYE</span>
            </div>
          </Link>
        </div>

        {/* Nav Items */}
        <nav className="p-4 space-y-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.href);
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium
                  transition-all duration-200 border
                  ${active 
                    ? 'bg-blue-500/10 text-blue-400 border-blue-500/20 shadow-[inset_0_0_8px_rgba(59,130,246,0.05)]' 
                    : 'text-gray-400 border-transparent hover:text-white hover:bg-blue-500/5'
                  }
                `}
              >
                <Icon className="w-5 h-5" />
                <span>{item.name}</span>
                {active && <ChevronRight className="w-4 h-4 ml-auto text-blue-400" />}
              </Link>
            );
          })}

          <div className="pt-4 border-t border-blue-500/10 mt-4">
            <Link
              to="/dashboard"
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-gray-400 hover:text-white hover:bg-blue-500/5"
            >
              <LayoutDashboard className="w-5 h-5" />
              <span>User Dashboard</span>
            </Link>
          </div>
        </nav>

        {/* User Section */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-blue-500/10">
          <div className="flex items-center gap-3 mb-4">
            <Avatar className="w-10 h-10 border border-blue-500/20">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-blue-500/20 text-blue-400">
                {user?.name?.charAt(0) || 'A'}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium truncate text-white">{user?.name || 'Admin'}</p>
                <Shield className="w-3 h-3 text-blue-400" />
              </div>
              <p className="text-xs text-gray-500 truncate">{user?.email}</p>
            </div>
          </div>
          <Button 
            onClick={logout}
            variant="ghost" 
            className="w-full justify-start text-gray-400 hover:text-red-400 hover:bg-red-500/10 hover:border-none"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
        <div className="p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
}
